import { RootState } from '../../store'
import { format, subMonths, isWithinInterval, startOfYear, endOfYear } from 'date-fns'

// Existing selectors
export const selectTotalEvents = (state: RootState) => state.eventDetailsReport.totalEvents
export const selectTotalActiveEvents = (state: RootState) => state.eventDetailsReport.totalActiveEvents
export const selectTotalNotActiveEvents = (state: RootState) =>
  state.eventDetailsReport.totalNotActiveEvents
export const selectTotalCompletedEvents = (state: RootState) =>
  state.eventDetailsReport.totalCompletedEvents
export const selectTotalPerCategory = (state: RootState) => state.eventDetailsReport.totalPerCategory
export const selectTotalInvitedTeamMember = (state: RootState) =>
  state.eventDetailsReport.totalInvitedTeamMember
export const selectListOfEvents = (state: RootState) => state.eventDetailsReport.listOfEvents

// Loading and error selectors
export const selectEventDetailsReportLoading = (state: RootState) => state.eventDetailsReport.loading
export const selectEventDetailsReportError = (state: RootState) => state.eventDetailsReport.error

// Time range filter types
export type EventRegistrationTimeRange = 'past3Months' | 'past6Months' | 'lastYear' | 'all'

// New selector for monthly registered and unregistered events with time range filtering
export const selectMonthlyEventRegistration = (
  state: RootState,
  timeRange: EventRegistrationTimeRange = 'past3Months',
) => {
  const events = state.eventDetailsReport.listOfEvents
  const now = new Date()

  // Determine the start and end dates based on the time range
  const getDateRange = () => {
    switch (timeRange) {
      case 'past3Months':
        return {
          start: subMonths(now, 3),
          end: now,
        }
      case 'past6Months':
        return {
          start: subMonths(now, 6),
          end: now,
        }
      case 'lastYear':
        return {
          start: startOfYear(new Date(now.getFullYear() - 1, 0, 1)),
          end: endOfYear(new Date(now.getFullYear() - 1, 11, 31)),
        }
      case 'all':
        return {
          start: new Date(0), // Start from epoch
          end: now,
        }
      default:
        return {
          start: new Date(0),
          end: now,
        }
    }
  }

  const { start: startDate, end: endDate } = getDateRange()

  // Filter events based on the time range
  const filteredEvents = events.filter((event) =>
    isWithinInterval(new Date(event.startDate), {
      start: startDate,
      end: endDate,
    }),
  )

  // Group filtered events by month and year
  const monthlyRegistration = filteredEvents.reduce(
    (acc, event) => {
      const monthKey = format(new Date(event.startDate), 'yyyy-MM')

      if (!acc[monthKey]) {
        acc[monthKey] = {
          total: 0,
          registered: 0,
          unregistered: 0,
        }
      }

      const eventTotal = event.registeredTeamMembers + event.unregisteredTeamMembers
      acc[monthKey].total += eventTotal
      acc[monthKey].registered += event.registeredTeamMembers
      acc[monthKey].unregistered += event.unregisteredTeamMembers

      return acc
    },
    {} as Record<
      string,
      {
        total: number
        registered: number
        unregistered: number
      }
    >,
  )

  // Convert to percentages
  const monthlyRegistrationPercentage = Object.entries(monthlyRegistration).reduce(
    (acc, [monthKey, data]) => {
      acc[monthKey] = {
        total: 100,
        registered: data.total > 0 ? Math.round((data.registered / data.total) * 100) : 0,
        unregistered: data.total > 0 ? Math.round((data.unregistered / data.total) * 100) : 0,
      }
      return acc
    },
    {} as Record<
      string,
      {
        total: number
        registered: number
        unregistered: number
      }
    >,
  )

  return monthlyRegistrationPercentage
}
